import React, { useState, useEffect } from "react";
import { Redirect, Route } from "react-router-dom";
import { useUser } from "../../UserContext";
import SkeletonLoading from "./SkeletonLoading";
import { useAccess } from "../../security/PermissionsContext";
import { getAuthToken, usesLocalApi } from "../../services/deploymentConfig";

/**
 * Waits for:
 *  - Firebase auth check
 *  - User + hospitalDetails in context
 *  - Permissions map (when `allowed` is provided)
 * Then enforces (module, action) permission.
 */
const ProtectedRoute = ({ component: Component, allowed, action = "view", ...rest }) => {
  const { user, hospitalDetails } = useUser();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const { can, perms } = useAccess();

  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;
    if (usesLocalApi()) {
      setFirebaseUser(null);
      setIsAuthReady(true);
      return () => {};
    }
    const init = async () => {
      const [{ onAuthStateChanged }, { auth }] = await Promise.all([
        import("firebase/auth"),
        import("../../firebase/firebase"),
      ]);
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, (fbUser) => {
        setFirebaseUser(fbUser || null);
        setIsAuthReady(true);
      });
    };
    init();
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <Route
      {...rest}
      render={(props) => {
        const localMode = usesLocalApi();
        const localToken = getAuthToken();
        const adminLike =
          user?.isOwner ||
          user?.isSuperAdmin ||
          user?.permissions?.includes?.("*") ||
          /^(admin|administrator)$/i.test(user?.roleName || "") ||
          /^(admin|administrator)$/i.test(user?.roleId || "");

        const permsReady = Boolean(perms) || adminLike;

        const contextStillLoading = localMode
          ? (localToken && user && (!hospitalDetails || (allowed && !permsReady)))
          : (
              !isAuthReady ||
              (firebaseUser && (!user || !hospitalDetails)) ||
              (firebaseUser && allowed && !permsReady)
            );

        if (contextStillLoading) return <SkeletonLoading />;

        const isSessionValid = localMode
          ? !!localToken && !!user && !!hospitalDetails
          : !!firebaseUser && !!user && !!hospitalDetails;
        if (!isSessionValid) return <Redirect to="/login" />;

        const permitted = allowed ? can(allowed, action) : true;
        return permitted ? <Component {...props} /> : <Redirect to="/unauthorized" />;
      }}
    />
  );
};

export default ProtectedRoute;
